import {postObjectWithApiError, getObject, IResultAndError} from '../../lib/ChoiceApi';

export enum guidLineType {
    Patient = 1,
    Provider = 2
};
export enum medInformationType { 
    Disclaimer = "Disclaimer",
    InteractionComments = "InteractionComments",
    WebLinks = "WebLinks",
    ProviderGuidelines = "ProviderGuidelines",
    PatientGuidelines = "PatientGuidelines",
    PatientEducation = "PatientEducation"
};
export enum medTab  {
    Allergy = "AllergyInteractions",
    Drug = "DrugInteractions",
    Disease = "DiseaseInteractions"
};
export enum interactionType {
    Drug = 1,
    Allergy = 2,
    Disease = 3
};
export enum severityLevel {
    NoneFound = -1, // No Interactions 
    None = 0, // No interactions
    Contraindicated = 1, // Contraindicated Drug Combination: This drug combination is contraindicated and generally should not be dispensed or administered
    Severe = 2, // Severe Interaction: Action is required to reduce the risk of severe adverse interaction.
    Moderate = 3, // Moderate Interaction: Assess the risk to the patient and take action as needed.
    Undetermined = 9, // Undetermined Severity - Alternative Therapy Interaction: Assess the risk to the patient and take action as needed.
    Unknown = 10, // Used for "made up" drugs...
    CalculationOnly = 99 // Used to set upper limit in order to get minimum value.
};
export enum severityClass {
    Level1 = "fa-solid fa-triangle-exclamation text-danger",
    Level2 = "fa-solid fa-triangle-exclamation text-warning",
    Level3 = "fa-solid fa-triangle-exclamation text-warning",
    Level9 = "fa-solid fa-circle-info text-primary",   
    Level10 = "fa-solid fa-circle-question text-warning"
};
export enum drugMonographType {
    MechanismOfAction = "A",
    Break = "B",
    Discussion = "D",
    ClinicalEffects = "E",
    SeverityLevel = "L",
    PatientManagment = "M",
    PredisposingFactors = "P",
    References = "R",
    MonographTitle = "T",
    InteractionDescription = "*"
};
export enum allergyMonographType {
    Allergen = 1,
    Ingredient = 2,
    IngredientBased = 3,
    InactiveIngredient = 4
};
export enum diseaseMonographType {
    MonographTitle = "T",
    Break = "B",
    References = "R",
    Contraindications = "C",
    Subreferences = "S"
};
export enum disclaimrMonographType {
    Important = "Z",
    Break = "B",
    InformationCopyright = "A",
    Notes = "N"
};
export enum educationMonographType {
    InformationCopyright = "A",
    Break = "B",
    CommonBrandName = "C", 
    MissedDose = "D",
    Pronunciation = "F", 
    HowToUse = "H",
    DrugInteractions = "I",
    MedicalAlert = "M",
    Notes = "N",
    Overdose = "O",
    Precautions = "P",
    Storage = "R",
    SideAffects = "S",
    DrugName = "T",
    Uses = "U",
    OtherUsers = "V",
    Warning = "W",
    Important = "Z"
};
export enum printClass {
    mutedTitle = "font-weight: 300;  color: #6c757d; font-size: .75rem; padding-top: 1rem;",
    muted = "color: #6c757d;",
    recordTitle = "font-weight: 700; color: #6A8CCB; font-size: 1rem; padding-bottom: .5rem;",
    patientData = "font-size: .8rem; padding-bottom: .5rem; padding-left: 1rem;",
    reportTitle = "font-weight: 700; color: #6A8CCB; font-size: 1.25rem; padding-bottom: .5rem;",
    reportDrugName = "font-weight: 300; font-size: 1rem;",
    allergenMonograph = "font-weight: 700; font-size: .75rem; padding-top: 1rem;",
    reportLeft1 = "padding-left: 1rem;",
    reportLeft2 = "padding-left: 1.5rem;",
    reportLeft3 = "padding-left: .75rem;",
    diseaseMonograph = "font-weight: 300; font-size: .75rem; padding-top: 1rem;",
    drugMonograph = "font-weight: 300; color: #6A8CCB; font-size: .75rem; padding-top: 0.5rem;",
    pageBreak = "page-break-before: always; color: #6c757d; padding-bottom: .25rem;",
    pageColumn1 = "padding-left: .25rem;",
    pageColumn2 = "border-left: 1px solid silver; padding-left: .25rem;",
};
export enum errorReturnMessage {
    disclaimer = "Unable to obtain disclaimer information.",
    comments = "Unable to obtain interaction comment information",
    weblinks = "Unable to obtain weblink information",
    guidelines = "Unable to obtain guideline information",
    patientEducation = "Unable to obtain patient education information",
    drugInteraction = "Unable to obtain drug interaction information",
    allergyInteraction = "Unable to obtain allergy interaction information",
    diseaseInteraction = "Unable to obtain disease interaction information"
};
export const reportSectionBreak : string = `<br/><div style="border-top: 3px solid silver;"></div><br/>`;
export const no_drug_information : string = "No information for this drug is available.";
export const reportPage1Rows : number = 60;
export const reportPage2Rows : number = 95; 
export interface IMedInformationInput {
    InformationType: medInformationType,
    PatientID: number,
    PrescriptionID?: number,
    MedID?: number, 
};

export interface IWebLinkInputs {
    PrescriptionID?: number,
    MedID?: number,
    Url: string,
    UrlDescription: string,
};
export interface IInteractionCommentInputs { 
    PrescriptionID: number,
    Comment: string
};
export interface IInteractionInput {
    PatientID: number,
    PrescriptionID?: number,
    MedID?: number,
    MedLevel?: number,
    DisplayAllInteractions?: number
};
export interface IInteractionLevel {
    drugMedID: number,
    drugMedLevel: number,
    drugName: string,
    minSeverityLevel: severityLevel
};
export interface IDrugInteraction {
    recordID: number,
    drugSeverityLevel: severityLevel,
    monographIdentifier: number,
    interactionDescription: string,
    monograph: string
};
export interface IDiseaseInteraction {
    recordID: number,
    monographType: string,
    monograph: string
};
export interface IAllergyInteraction {
    recordID: number,
    monographType: string,
    monograph: string
};
export interface IMedPatientEducation {
    recordID : number,
    monographType: string,
    monograph : string
};
export interface IMedGuideline {
    recordID : number,
    monograph : string
};
export interface IRxWebLink {
    url : string,
    description : string,
    creationData? : string
};
export interface IRxInteractionComment {
    userName : string,
    localCommentDate : string,
    comment : string
};
export interface IFdbDisclaimer {
    recordID: number,
    monographType: string,
    monograph: string
};  
export interface IPatientRxDemographics {
    patientID: number,
    fullName?: string,
    age?: string,
    gender?: string,
    streetAddress?: string,
    address?: string,
    homePhone?: string
};  
export interface IInteractionDrugResponse {
    interactionlData: IInteractionLevel,
    drugList: IDrugInteraction[]
};
export interface IInteractionDiseaseResponse {
    interactionlData: IInteractionLevel,
    diseaseList: IDiseaseInteraction[]
};
export interface IInteractionAllergyResponse {
    interactionlData: IInteractionLevel,
    allergyList: IAllergyInteraction[]
};
export interface IMedPatientEducationResponse {
    educationList: IMedPatientEducation[]
};
export interface IMedGuidelineResponse {
    guidelineList: IMedGuideline[]
};
export interface IMedWebLinkResponse {
    weblinkList: IRxWebLink[]
};
export interface IMedInteractionResponse {
    interactionList: IRxInteractionComment[]
};
export interface IFdbDisclaimerResponse {
    patientData: IPatientRxDemographics,
    disclaimerList: IFdbDisclaimer[]
};
export interface IUpdateWebLinkResponse {
    errorCode: number,
    errorMessage?: string 
};
export interface IUpdateInteractionCommentResponse {
    errorCode: number,
    errorMessage?: string 
};
export async function UpdateWebLink(payLoad: IWebLinkInputs): Promise<IResultAndError<IUpdateWebLinkResponse>>{
    return postObjectWithApiError("api/Prescriptions/Interaction/UpdateWebLink", payLoad);
};
export async function UpdateInteractionComment(payLoad: IInteractionCommentInputs): Promise<IResultAndError<IUpdateWebLinkResponse>>{
    return postObjectWithApiError("api/Prescriptions/Interaction/UpdateInteractionComment", payLoad);
};
export async function GetDrugInteraction(payLoad: IInteractionInput): Promise<IResultAndError<IInteractionDrugResponse>>{  
    let returnErrorMessage = "";
    const url = buildInteractionURL("GetDrugInteraction",payLoad);
    const responseObject = await getObject<IInteractionDrugResponse>(url,(httResp, errorMessage) =>  { 
        returnErrorMessage = errorMessage ?? errorReturnMessage.drugInteraction;
    });
    if (responseObject)
        return {success: responseObject};
    else 
        return {error: {error: returnErrorMessage}};
}
export async function GetDiseaseInteraction(payLoad: IInteractionInput): Promise<IResultAndError<IInteractionDiseaseResponse>>{  
    let returnErrorMessage = "";
    const url = buildInteractionURL("GetDiseaseInteraction",payLoad);
    const responseObject = await getObject<IInteractionDiseaseResponse>(url,(httResp, errorMessage) =>  { 
        returnErrorMessage = errorMessage ?? errorReturnMessage.diseaseInteraction;
    });
    if (responseObject)
        return {success: responseObject};
    else 
        return {error: {error: returnErrorMessage}}; 
} 
export async function GetAllergyInteraction(payLoad: IInteractionInput): Promise<IResultAndError<IInteractionAllergyResponse>>{  
    let returnErrorMessage = "";
    const url = buildInteractionURL("GetAllergyInteraction",payLoad);
    const responseObject = await getObject<IInteractionAllergyResponse>(url,(httResp, errorMessage) =>  { 
        returnErrorMessage = errorMessage ?? errorReturnMessage.allergyInteraction;
    });
    if (responseObject)
        return {success: responseObject};
    else 
        return {error: {error: returnErrorMessage}};
} 
export async function GetMedPatientEducation(payLoad: IMedInformationInput): Promise<IResultAndError<IMedPatientEducationResponse>>{  
    let returnErrorMessage = "";
    const url = buildMedInformationURL("GetMedPatientEducation",payLoad);
    const responseObject = await getObject<IMedPatientEducationResponse>(url,(httResp, errorMessage) =>  { 
        returnErrorMessage = errorMessage ?? errorReturnMessage.guidelines;
    });
    if (responseObject)
        return {success: responseObject};
    else 
        return {error: {error: returnErrorMessage}};
}
export async function GetMedGuideline(payLoad: IMedInformationInput): Promise<IResultAndError<IMedGuidelineResponse>>{  
    let returnErrorMessage = "";
    const url = buildMedInformationURL("GetMedGuideline",payLoad);
    const responseObject = await getObject<IMedGuidelineResponse>(url,(httResp, errorMessage) =>  { 
        returnErrorMessage = errorMessage ?? errorReturnMessage.guidelines;
    });
    if (responseObject)
        return {success: responseObject};
    else 
        return {error: {error: returnErrorMessage}};
}
export async function GetMedWeblink(payLoad: IMedInformationInput): Promise<IResultAndError<IMedWebLinkResponse>>{  
    let returnErrorMessage = "";
    const url = buildMedInformationURL("GetMedWeblink",payLoad);
    const responseObject = await getObject<IMedWebLinkResponse>(url,(httResp, errorMessage) =>  { 
        returnErrorMessage = errorMessage ?? errorReturnMessage.comments;
    });
    if (responseObject)
        return {success: responseObject};
    else 
        return {error: {error: returnErrorMessage}};
}
export async function GetMedInteractionComment(payLoad: IMedInformationInput): Promise<IResultAndError<IMedInteractionResponse>>{  
    let returnErrorMessage = "";
    const url = buildMedInformationURL("GetMedInteractionComment",payLoad);
    const responseObject = await getObject<IMedInteractionResponse>(url,(httResp, errorMessage) =>  { 
        returnErrorMessage = errorMessage ?? errorReturnMessage.comments;
    });
    if (responseObject)
        return {success: responseObject};
    else 
        return {error: {error: returnErrorMessage}}; 
}
export async function GetFdbDisclaimer(payLoad: IMedInformationInput): Promise<IResultAndError<IFdbDisclaimerResponse>>{  
    let returnErrorMessage = "";
    const url = buildMedInformationURL("GetFdbDisclaimer",payLoad);
    const responseObject = await getObject<IFdbDisclaimerResponse>(url,(httResp, errorMessage) =>  { 
        returnErrorMessage = errorMessage ?? errorReturnMessage.disclaimer;
    });
    if (responseObject)
        return {success: responseObject};
    else 
        return {error: {error: returnErrorMessage}};
}
function buildMedInformationURL(functionName : string, payLoad: IMedInformationInput) : string {
    return `api/Prescriptions/Interaction/${functionName}?InformationType=${encodeURI(payLoad.InformationType)}&PatientID=${payLoad.PatientID}&PrescriptionID=${payLoad.PrescriptionID}&MedID=${payLoad.MedID}`;
}
function buildInteractionURL(functionName : string, payLoad: IInteractionInput) : string {
    return `api/Prescriptions/Interaction/${functionName}?PatientID=${payLoad.PatientID}&PrescriptionID=${payLoad.PrescriptionID}&MedID=${payLoad.MedID}&MedLevel=${payLoad.MedLevel}&DisplayAllInteractions=${payLoad.DisplayAllInteractions}`;
}
export function getSeverityClass(severity : severityLevel) : string {
    const enumValue =  `Level${severity}`;
    return severityClass[enumValue as keyof typeof severityClass];
}
export function getSeverityDescription(severity : severityLevel) : string { 
    return severityLevel[severity.toString() as keyof typeof severityLevel].toString();
}
export function removeBadChars(inputString: string, charsToRemove: string): string { 
    const charsRegExp = new RegExp(`[${charsToRemove}]`, 'g'); 
    const outputString = inputString.replace(charsRegExp, ''); 
    return outputString;
}
export function buildDoubleColumnReport(pagetitle: string, drugName: string, page1Rows : number, pageNRows: number, inputData : string[]) : string  {
    if (inputData.length === 0) {
        return "";
    }  
    let table_count = 0;
    let page_counter = 1;
    let return_data = [];
    const table_html = [];
 
    return_data = inputData.splice(0, page1Rows * 2);  
    table_html[table_count++] = `<div style="${printClass.recordTitle}">${pagetitle}</div>`;
    table_html[table_count++] = buildDoubleColumnPage(return_data,false,page_counter,pagetitle,drugName);

    while (inputData.length > 0) { 
        return_data = inputData.splice(0, pageNRows * 2);
        page_counter += 2;
        table_html[table_count++] = buildDoubleColumnPage(return_data,true,page_counter,pagetitle,drugName);
    } 
    return table_html.join("");
}
function buildDoubleColumnPage(pageData : string[], pageBreak : boolean,pageCounter : number,pagetitle: string, drugName: string) : string {
    const return_count = pageData.length;
    const table_html = 
        (pageBreak ? `<div style="${printClass.pageBreak}">Pages ${pageCounter}/${(pageCounter + 1)}: ${pagetitle} for ${drugName}</div>` : "") +
        "<table style=\"width: 90%; table-layout: fixed\"><colgroup><col style=\"width: 50%;\"><col style=\"width: 50%;\">" +    
            "<tr>" +
                "<td style=\"" + printClass.pageColumn1 + "\">" + pageData.splice(0,return_count / 2).join("") + "</td>" +
                "<td style=\"" + printClass.pageColumn2 + "\">" + pageData.join("") + "</td>" +
            "</tr>" +
        "</table>";
    return table_html; 
}
export function buildSingleColumnReport(pagetitle: string, drugName: string, page1Rows : number, pageNRows: number, inputData : string[]) : string  {
    if (inputData.length === 0) {
        return "";
    }  
    let table_count = 0;
    let page_counter = 1;
    let return_data = [];
    const table_html = [];
 
    return_data = inputData.splice(0, page1Rows);  
    table_html[table_count++] = `<div style="${printClass.recordTitle}">${pagetitle}</div>`;
    table_html[table_count++] = buildSingleColumnPage(return_data,false,page_counter,pagetitle,drugName);

    while (inputData.length > 0) { 
        return_data = inputData.splice(0, pageNRows);
        page_counter++;
        table_html[table_count++] = buildSingleColumnPage(return_data,true,page_counter,pagetitle,drugName);
    } 
    return table_html.join("");
}
function buildSingleColumnPage(pageData : string[], pageBreak : boolean,pageCounter : number, pagetitle: string, drugName: string) : string {
    const table_html = 
        (pageBreak ? `<div style="${printClass.pageBreak}">Pages ${pageCounter}/${(pageCounter + 1)}: ${pagetitle} for ${drugName}</div>` : "") +
        "<table><colgroup><col>" +    
            "<tr>" +
                "<td style=\"" + printClass.pageColumn1 + "\">" + pageData.join("") + "</td>" +
            "</tr>" +
        "</table>";
    return table_html; 
}